import React, { ReactNode } from "react";
import { Image } from "react-bootstrap";
import {
  RewardStyle,
  Tournament,
  TournamentStatus,
} from "@nestcoinco/hyper-api-gateway-api-client";
import { useTranslation } from "react-i18next";

import "./TournamentCard.scss";
import ImageHyperCoin from "../../../images/hyper-coin.svg";
import ImageCashReward from "../../../images/cash-reward.svg";
import PersonFilledIcon from "../../icons/PersonFilledIcon";
import StopwatchIcon from "../../icons/StopwatchIcon";
import UsersIcon from "../../icons/UsersIcon";
import useCountdown from "../../../hooks/useCountdown";
import useHprFiatFormatter from "../../../hooks/useHprFiatFormatter";
import { cx } from "../../../utils/cx";
import {
  getTournamentParticipationSummary,
  shouldShowTournamentRewardRange,
} from "../../../utils/tournament";

const PRIZE_POOL_ABBREVIATION_THRESHOLD = 5;

function TournamentPoster({
  src,
  name,
}: {
  src?: string;
  name?: string;
}) {
  const { t } = useTranslation("dashboard");

  return (
    <Image
      src={src}
      alt={name ?? t("game_poster")}
      className="tournament-poster"
    />
  );
}

function CardCountdownValue({ timeLeft }: { timeLeft?: string }) {
  const { t } = useTranslation("dashboard");
  const [hours, minutes, seconds] = timeLeft?.split(":") ?? [];
  if (!hours || !minutes || !seconds) {
    return (
      <span className="tournament-countdown-value">{timeLeft || "--"}</span>
    );
  }

  const hoursValue = Number(hours);
  const showHours = hoursValue >= 1;

  return (
    <span className="tournament-countdown-value">
      {showHours && (
        <span className="tournament-countdown-unit">
          <span className="tournament-countdown-digits tournament-countdown-digits--hours">
            {String(hoursValue).padStart(2, "0")}
          </span>
          {t("countdown_unit_h")}
        </span>
      )}
      <span className="tournament-countdown-unit">
        <span className="tournament-countdown-digits">
          {minutes.padStart(2, "0")}
        </span>
        {t("countdown_unit_m")}
      </span>
      <span className="tournament-countdown-unit">
        <span className="tournament-countdown-digits">
          {seconds.padStart(2, "0")}
        </span>
        {t("countdown_unit_s")}
      </span>
    </span>
  );
}

function CardCountdownBadge({
  labelKey,
  timeLeft,
}: {
  labelKey: string;
  timeLeft?: string;
}) {
  const { t } = useTranslation("dashboard");

  return (
    <div className="tournament-badge tournament-badge-countdown">
      <StopwatchIcon aria-hidden="true" />
      <span>
        {t(labelKey, { countdown: "" }).trim()}{" "}
        <CardCountdownValue timeLeft={timeLeft} />
      </span>
    </div>
  );
}

function TournamentPrizePool({
  minimumRewardAmount,
  rewardAmount,
  rewardStyle,
}: {
  minimumRewardAmount?: number;
  rewardAmount?: number;
  rewardStyle?: RewardStyle;
}) {
  const { isFiatDisplay, formatHprToFiatValue } = useHprFiatFormatter();
  const showRewardRange = shouldShowTournamentRewardRange(rewardStyle);
  const maxReward = formatHprToFiatValue(
    rewardAmount,
    PRIZE_POOL_ABBREVIATION_THRESHOLD
  );
  const minReward = formatHprToFiatValue(
    minimumRewardAmount,
    PRIZE_POOL_ABBREVIATION_THRESHOLD
  );

  if (!maxReward || maxReward === "--") {
    return <p className="prize-pool-content">--</p>;
  }

  const renderValue = (formatted: string) =>
    isFiatDisplay ? (
      <span className="prize-pool-amount">{formatted}</span>
    ) : (
      <>
        <Image
          src={ImageHyperCoin}
          alt=""
          width={24}
          height={24}
          className="prize-pool-coin"
        />
        <span className="prize-pool-amount">{formatted}</span>
      </>
    );

  return (
    <p className={cx("prize-pool-content", isFiatDisplay && "fiat")}>
      {showRewardRange && minReward && minReward !== "--" && (
        <>
          {renderValue(minReward)}
          <span className="prize-pool-range-separator">-</span>
        </>
      )}
      {renderValue(maxReward)}
    </p>
  );
}

function CardVictoryPot({
  minimumRewardAmount,
  rewardAmount,
  rewardStyle,
  action,
}: {
  minimumRewardAmount?: number;
  rewardAmount?: number;
  rewardStyle?: RewardStyle;
  action?: ReactNode;
}) {
  const { t } = useTranslation("dashboard");

  return (
    <div className="victory-pot-block">
      <div className="prize-pool-header">
        <div className="prize-pool-label">
          <Image
            src={ImageCashReward}
            alt=""
            aria-hidden="true"
            className="prize-pool-reward-image"
          />
          <h6 className="prize-pool-heading">{t("victory_pot")}</h6>
        </div>
        {action}
      </div>
      <TournamentPrizePool
        minimumRewardAmount={minimumRewardAmount}
        rewardAmount={rewardAmount}
        rewardStyle={rewardStyle}
      />
    </div>
  );
}

function CardCta({
  labelKey,
  isEnded,
  isStartingSoon,
}: {
  labelKey: string;
  isEnded?: boolean;
  isStartingSoon?: boolean;
}) {
  const { t } = useTranslation("dashboard");

  return (
    <span
      className={cx(
        "play-now-text",
        isStartingSoon && "starting-soon",
        isEnded && "ended"
      )}
    >
      {t(labelKey)}
    </span>
  );
}

function CardStakeBadge({ stakeAmount }: { stakeAmount: number }) {
  const { isFiatDisplay, formatHprToFiatValue } = useHprFiatFormatter();
  const stakeAmountValue = formatHprToFiatValue(stakeAmount);

  return (
    <div className={cx("tournament-badge", isFiatDisplay && "fiat")}>
      {!isFiatDisplay && (
        <Image src={ImageHyperCoin} alt="" width={16} height={16} />
      )}
      <span>{stakeAmountValue || "--"}</span>
    </div>
  );
}

function CardSlotsBadge({
  summary,
}: {
  summary: ReturnType<typeof getTournamentParticipationSummary>;
}) {
  const { t } = useTranslation("dashboard");
  const {
    joinedCount,
    hasLowSlots,
    shouldShowJoinedCount,
    isAtCapacity,
    participantLimit,
    slotsLeft,
  } = summary;

  return (
    <div className="tournament-badge">
      {isAtCapacity || hasLowSlots ? (
        <PersonFilledIcon aria-hidden="true" />
      ) : (
        <UsersIcon aria-hidden="true" />
      )}
      <span>
        {isAtCapacity && t("filled")}
        {!isAtCapacity && hasLowSlots && t("slots_left", { count: slotsLeft })}
        {!isAtCapacity &&
          !hasLowSlots &&
          (shouldShowJoinedCount ? joinedCount : participantLimit)}
      </span>
    </div>
  );
}

function CardShell({
  isEnded,
  className,
  children,
}: {
  isEnded?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cx("TournamentCard", isEnded && "ended", className)}>
      {children}
    </div>
  );
}

interface BaseCardProps {
  tournament: Tournament;
  className?: string;
}

function ScheduledOrOpenTournamentCard({
  tournament,
  className,
}: BaseCardProps) {
  const {
    game,
    minimumRewardAmount,
    rewardAmount,
    rewardStyle,
    endDate,
    startDate,
    stakeAmount,
    status,
  } = tournament;
  const summary = getTournamentParticipationSummary(tournament);
  const isLive = status === TournamentStatus.ONGOING;
  const timeLeft = useCountdown({
    futureDateOrTotalTimeInMs: (isLive ? endDate : startDate) || "",
    showHours: true,
  });

  return (
    <CardShell className={className}>
      <TournamentPoster src={game?.poster} name={game?.name} />
      <div className="tournament-content">
        <div className="tournament-top">
          <CardVictoryPot
            minimumRewardAmount={minimumRewardAmount}
            rewardAmount={rewardAmount}
            rewardStyle={rewardStyle}
            action={
              <CardCta
                labelKey={isLive ? "play_now" : "starting_soon"}
                isStartingSoon={!isLive}
              />
            }
          />
        </div>
        <div className="tournament-bottom">
          <h4 className="title">{game?.name}</h4>
          <div className="tournament-badges">
            <CardCountdownBadge
              labelKey={isLive ? "ends_in" : "starts_in"}
              timeLeft={timeLeft}
            />
            <CardSlotsBadge summary={summary} />
            <CardStakeBadge stakeAmount={stakeAmount} />
          </div>
        </div>
      </div>
    </CardShell>
  );
}

function CompletedTournamentCard({
  tournament,
  className,
}: BaseCardProps) {
  const { t } = useTranslation("dashboard");
  const { isFiatDisplay, formatHprToFiatValue } = useHprFiatFormatter();
  const { game, topRewards } = tournament;
  const summary = getTournamentParticipationSummary(tournament);
  const reward = formatHprToFiatValue(topRewards?.[0]);

  return (
    <CardShell isEnded className={className}>
      <TournamentPoster src={game?.poster} name={game?.name} />
      <div className="tournament-content">
        <div className="tournament-top">
          <CardCta labelKey="ended" isEnded />
        </div>
        <div className="tournament-bottom">
          <h4 className="title">{game?.name}</h4>
          <div className="ended-tournament-summary">
            <div className="ended-tournament-summary-item">
              <span className="ended-tournament-summary-label">
                {t("total_players")}
              </span>
              <strong>{summary.joinedCount}</strong>
            </div>
            <div className="ended-tournament-summary-item">
              <span className="ended-tournament-summary-label">
                {t("first_place_won")}
              </span>
              <strong
                className={cx("ended-tournament-reward", isFiatDisplay && "fiat")}
              >
                {!isFiatDisplay && (
                  <Image src={ImageHyperCoin} alt="" width={16} height={16} />
                )}
                {reward || "--"}
              </strong>
            </div>
          </div>
        </div>
      </div>
    </CardShell>
  );
}

export interface TournamentCardProps {
  tournament: Tournament;
  className?: string;
}

function TournamentCard({ tournament, ...props }: TournamentCardProps) {
  if (tournament.status === TournamentStatus.COMPLETED) {
    return <CompletedTournamentCard {...props} tournament={tournament} />;
  }

  return <ScheduledOrOpenTournamentCard {...props} tournament={tournament} />;
}

export default TournamentCard;
